import { HttpErrorResponse } from '@angular/common/http';
import { Result } from '@core/models/common.model';
import {
  API_ERROR_FALLBACKS,
  API_ERROR_MESSAGES,
  API_SUCCESS_MESSAGES,
  ApiErrorFallbackKey,
  ApiSuccessMessageKey,
} from '@core/constants/messages/api-messages.constants';

type ValidationErrorItem = {
  field?: string;
  message?: string;
};

type ErrorLike = {
  message?: string;
  errorCode?: string;
  errors?: Record<string, string[]>;
  error?: {
    code?: string;
    message?: string;
    details?: string;
    validationErrors?: ValidationErrorItem[];
  };
};

const GENERIC_BACKEND_MESSAGES = new Set([
  'unexpected error',
  'unknown error',
  'internal server error',
  'error',
  'backend returned an error',
]);

function isGenericBackendMessage(message?: string): boolean {
  if (!message?.trim()) {
    return true;
  }

  return GENERIC_BACKEND_MESSAGES.has(message.trim().toLowerCase());
}

function mapKnownDetailCode(details: string): string | undefined {
  const normalized = details.toUpperCase();

  if (normalized.includes('EMAIL_EXISTS')) {
    return API_ERROR_MESSAGES['EMAIL_EXISTS'];
  }

  if (normalized.includes('PHONE_NUMBER_EXISTS') || normalized.includes('MOBILE_NUMBER_EXISTS')) {
    return API_ERROR_MESSAGES['MOBILE_NUMBER_EXISTS'];
  }

  return undefined;
}

function resolveDetailsMessage(details?: string): string | undefined {
  if (!details?.trim()) {
    return undefined;
  }

  const knownCodeMessage = mapKnownDetailCode(details);
  if (knownCodeMessage) {
    return knownCodeMessage;
  }

  const trimmed = details.trim();
  const withoutFirebasePrefix = trimmed.replace(/^Error creating Firebase user:\s*/i, '').trim();

  return withoutFirebasePrefix || trimmed;
}

export function extractApiValidationErrors(payload: ErrorLike): Record<string, string[]> | undefined {
  const validationErrors = payload.error?.validationErrors;
  if (!validationErrors?.length) {
    return payload.errors;
  }

  const errors: Record<string, string[]> = { ...(payload.errors || {}) };

  validationErrors.forEach((item) => {
    const field = item.field?.trim();
    const message = item.message?.trim();
    if (!field || !message) {
      return;
    }

    errors[field] = [...(errors[field] || []), message];
  });

  return Object.keys(errors).length ? errors : undefined;
}

function formatValidationErrorMessage(errors: Record<string, string[]>): string {
  return Object.values(errors)
    .flat()
    .filter(Boolean)
    .join(' ');
}

export function getApiErrorMessage(
  source: unknown,
  fallback: string = API_ERROR_FALLBACKS.GENERIC,
): string {
  if (source instanceof HttpErrorResponse) {
    return getApiErrorMessage(parseHttpErrorBody(source), fallback);
  }

  if (!source || typeof source !== 'object') {
    return fallback;
  }

  const payload = source as ErrorLike;
  const errorCode = payload.error?.code || payload.errorCode;
  const mappedMessage = errorCode ? API_ERROR_MESSAGES[errorCode] : undefined;

  if (mappedMessage) {
    return mappedMessage;
  }

  const validationErrors = extractApiValidationErrors(payload);
  if (validationErrors) {
    return formatValidationErrorMessage(validationErrors);
  }

  const detailsMessage = resolveDetailsMessage(payload.error?.details);
  if (detailsMessage) {
    return detailsMessage;
  }

  const nestedMessage = payload.error?.message;
  if (typeof nestedMessage === 'string' && nestedMessage.trim() && !isGenericBackendMessage(nestedMessage)) {
    return nestedMessage.trim();
  }

  if (typeof payload.message === 'string' && payload.message.trim() && !isGenericBackendMessage(payload.message)) {
    return payload.message.trim();
  }

  if (errorCode?.startsWith('HTTP_') && API_ERROR_MESSAGES[errorCode]) {
    return API_ERROR_MESSAGES[errorCode];
  }

  return fallback;
}

export function getApiSuccessMessage(
  key: ApiSuccessMessageKey,
  params?: Record<string, string | number>,
): string {
  const template = API_SUCCESS_MESSAGES[key];

  if (key === 'FEATURE_DELETED' && params?.['name']) {
    return `"${params['name']}" was deleted successfully.`;
  }

  if (key === 'PLAN_DELETED' && params?.['name']) {
    return `"${params['name']}" was deleted successfully.`;
  }

  if (key === 'FEATURE_STATUS_UPDATED' && params?.['status']) {
    return `Feature status updated to ${params['status']}.`;
  }

  if (key === 'PLAN_STATUS_UPDATED' && params?.['status']) {
    return `Plan status updated to ${params['status']}.`;
  }

  return template;
}

export function normalizeApiFailure<T>(
  response: unknown,
  fallbackKey: ApiErrorFallbackKey = 'GENERIC',
): Extract<Result<T>, { success: false }> {
  const fallback = API_ERROR_FALLBACKS[fallbackKey];
  const payload = (response ?? {}) as ErrorLike & { success?: boolean };
  const errorCode = payload.error?.code || payload.errorCode;
  const validationErrors = extractApiValidationErrors(payload);
  const error = payload.error
    ? {
        code: payload.error.code || errorCode || (validationErrors ? 'VALIDATION_FAILED' : 'UNKNOWN_ERROR'),
        message: payload.error.message || '',
        details: payload.error.details,
      }
    : undefined;

  return {
    success: false,
    message: getApiErrorMessage(payload, fallback),
    errorCode: errorCode || error?.code,
    errors: validationErrors,
    error,
  };
}

export function coerceApiResult<T>(
  response: unknown,
  fallbackKey: ApiErrorFallbackKey = 'GENERIC',
): Result<T> {
  if (response && typeof response === 'object' && 'success' in response) {
    const typed = response as Result<T>;
    return typed.success ? typed : normalizeApiFailure<T>(typed, fallbackKey);
  }

  return { success: true, data: response as T };
}

export function mapHttpErrorToResult<T>(
  error: unknown,
  fallbackKey: ApiErrorFallbackKey = 'GENERIC',
): Result<T> {
  if (error instanceof HttpErrorResponse) {
    return normalizeApiFailure<T>(parseHttpErrorBody(error), fallbackKey);
  }

  return normalizeApiFailure<T>(error, fallbackKey);
}

function parseHttpErrorBody(error: HttpErrorResponse): ErrorLike {
  const backend = error.error;

  if (backend && typeof backend === 'object') {
    return backend as ErrorLike;
  }

  return {
    message: typeof backend === 'string' ? backend : error.message,
    errorCode: `HTTP_${error.status || 'UNKNOWN'}`,
  };
}
